import { TextWithTooltip } from "@faire/retailer-visitor-shared/components/TextWithTooltip";
import { ExclusivityDetailsLink } from "@faire/retailer-visitor-shared/features/Exclusivity/DetailsModal/ExclusivityDetailsLink";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useSettingPrefetchPdpApis } from "@faire/retailer-visitor-shared/settings/useSettingFEPrefetchPdpApis";
import { useSettingInternationalBuyingNudgesV2 } from "@faire/retailer-visitor-shared/settings/useSettingInternationalBuyingNudgesV2";
import { buildFulfillmentTime } from "@faire/retailer-visitor-shared/views/brand/utils/leadTimeUtils";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web--source/common/localization";
import { useTraceComponentRender } from "@faire/web--source/common/tracing/useTraceComponentRender";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Button } from "@faire/web--source/slate/Button";
import { Information } from "@faire/web--source/slate/icons/foundation/Information";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IBrandViewV2Response } from "@faire/web-api--source/indigofair/data/IBrandViewV2Response";
import { IInformationMessage } from "@faire/web-api--source/indigofair/data/IInformationMessage";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import React, { useMemo } from "react";
import styled from "styled-components";

import { useFetchCachedBrandView } from "../../brand/hooks/useFetchCachedBrandView";

import { getShippingDetailsInfoTextIcon } from "./__internal__/getShippingDetailsInfoTextIcon";

type ShippingDetailsItem = {
  icon?: React.ReactNode;
  text: React.ReactNode;
  id: string;
};

type IShippingDetailsListProps = {
  title?: string;
  brand: IProductPageResponse.IPdpBrand;
  infoTexts?: IInformationMessage[];
  importShipmentInfo?: string;
  shippingPromotion?: IInformationMessage;
  isPreorder?: boolean;
  exclusivityAvailabilityAgreementTokenPromise: Promise<string | undefined>;
  isMobile: boolean;
};

const useEstimatedDeliveryMessage = (
  data: IBrandViewV2Response | undefined,
  isPreorder?: boolean
): IInformationMessage | undefined => {
  return useMemo(() => {
    return data?.info_texts?.find(
      (infoText: IInformationMessage) =>
        infoText.type === IInformationMessage.Type.GET_IT_BY && !isPreorder
    );
  }, [data, isPreorder]);
};

const createEstimatedDeliveryItem = (
  estimatedDeliveryMessage: IInformationMessage | undefined,
  isMobile: boolean
): ShippingDetailsItem | null => {
  if (
    !estimatedDeliveryMessage?.type ||
    !estimatedDeliveryMessage?.text ||
    !estimatedDeliveryMessage?.tooltip
  ) {
    return null;
  }

  return {
    id: estimatedDeliveryMessage.type,
    icon: getShippingDetailsInfoTextIcon(estimatedDeliveryMessage.type),
    text: (
      <TextWithTooltip
        text={estimatedDeliveryMessage.text}
        tooltipText={estimatedDeliveryMessage.tooltip}
        tooltipWidth={isMobile ? 200 : undefined}
      />
    ),
  };
};

const createPrefetchedEstimatedDeliveryItem = (
  brand: IProductPageResponse.IPdpBrand,
  isPreorder: boolean | undefined,
  strictLocalize: StrictLocalizeFunction,
  isMobile: boolean
): ShippingDetailsItem | null => {
  if (isPreorder || !brand.estimated_get_by_date_range_formatted) {
    return null;
  }

  const translatedTooltip = strictLocalize({
    defaultMessage:
      "This delivery estimate is based on the time it takes the brand to ship your order, and the time it takes the carrier to deliver it.",
    description: {
      text: "Tooltip text for the get it by shipping details",
      img: "https://cdn.faire.com/fastly/752f62324cee2a8b633e478483eb8d7c6e17095c0225f91928642d13c968b70f.png",
    },
  });

  return {
    id: IInformationMessage.Type.GET_IT_BY,
    icon: getShippingDetailsInfoTextIcon(IInformationMessage.Type.GET_IT_BY),
    text: (
      <TextWithTooltip
        text={brand.estimated_get_by_date_range_formatted ?? ""}
        tooltipText={translatedTooltip}
        tooltipWidth={isMobile ? 200 : undefined}
      />
    ),
  };
};

const createLeadTimeItem = (
  brand: IProductPageResponse.IPdpBrand,
  strictLocalize: StrictLocalizeFunction
): ShippingDetailsItem | null => {
  const hasLeadTimeAsRange =
    brand.upper_bound_lead_time_days !== brand.lower_bound_lead_time_days &&
    isNotUndefined(brand.lower_bound_lead_time_days) &&
    isNotUndefined(brand.upper_bound_lead_time_days);

  if (isLoggedInRetailerOrBrandPreview() || !hasLeadTimeAsRange) {
    return null;
  }

  return {
    id: "FULFILLMENT_TIME_CUSTOM",
    icon: getShippingDetailsInfoTextIcon("FULFILLMENT_TIME_CUSTOM"),
    text: (
      <Typography>
        {buildFulfillmentTime(
          {
            leadTimeDays: brand.lead_time_days,
            upperBoundLeadTimeDays: brand.upper_bound_lead_time_days,
            lowerBoundLeadTimeDays: brand.lower_bound_lead_time_days,
          },
          strictLocalize
        )}
      </Typography>
    ),
  };
};

const createInfoTextsItems = (
  infoTexts: IInformationMessage[] | undefined,
  brandToken: string | undefined,
  retailerToken: string | undefined,
  exclusivityAvailabilityAgreementToken: string | undefined
): ShippingDetailsItem[] => {
  if (!infoTexts) {
    return [];
  }

  return infoTexts.map((infoText, index) => ({
    id: infoText.type ?? `UNKNOWN_${index}`,
    icon: infoText.type ? getShippingDetailsInfoTextIcon(infoText.type) : null,
    text: (
      <Typography>
        {infoText.text}{" "}
        {retailerToken ? (
          <CustomLink
            linkUrl={infoText.help_link}
            brandToken={brandToken}
            type={infoText.type}
            ctaText={infoText.cta_text}
            exclusivityAvailabilityAgreementToken={
              exclusivityAvailabilityAgreementToken
            }
          />
        ) : null}
      </Typography>
    ),
  }));
};

const createImportShipmentInfoItem = (
  importShipmentInfo: string | undefined,
  isInternationalBuyingNudgesV2Enabled: boolean
): ShippingDetailsItem | null => {
  if (!importShipmentInfo || !isInternationalBuyingNudgesV2Enabled) {
    return null;
  }

  return {
    id: "IMPORT_SHIPMENT_INFO",
    icon: null,
    text: <Typography>{importShipmentInfo}</Typography>,
  };
};

const addShippingPromotion = (
  shipsFrom: ShippingDetailsItem[],
  shippingPromotion: IInformationMessage | undefined,
  isMobile: boolean
): void => {
  if (
    !shippingPromotion?.type ||
    !shippingPromotion?.text ||
    !shippingPromotion?.tooltip
  ) {
    return;
  }

  const promotionItem = {
    id: shippingPromotion.type,
    icon: getShippingDetailsInfoTextIcon(shippingPromotion.type),
    text: (
      <TextWithTooltip
        text={shippingPromotion.text}
        tooltipText={shippingPromotion.tooltip}
        tooltipWidth={isMobile ? 200 : undefined}
      />
    ),
  };

  shipsFrom.push(promotionItem);
};

const ShippingDetailsListContent: React.FC<{
  shipsFrom: ShippingDetailsItem[];
  title?: string;
}> = ({ shipsFrom, title }) => {
  return (
    <>
      <Spacer size="3x" />
      <Column>
        <Typography variant="paragraphSansMedium">
          {title ? (
            title
          ) : (
            <StrictLocalMsg
              defaultMessage="Shipping & policies"
              description={{
                text: "Title text above some shipping and other policy information for a product",
                img: "https://cdn.faire.com/fastly/5fbd830f06e5b9b3c7f2d0655df159adf1f6e7f3a623979b956e18034f696df7.png",
              }}
            />
          )}
        </Typography>
        <Spacer size={12} />
        <ul>
          {shipsFrom.map((item) => (
            <ListItemNoBullet key={`shipping-info-${item.id}`}>
              <Row gap="1x">
                <IconWrapper>{item.icon ?? <Information />}</IconWrapper>
                {item.text}
              </Row>
            </ListItemNoBullet>
          ))}
        </ul>
      </Column>
    </>
  );
};

export const ShippingDetailsList: React.FC<IShippingDetailsListProps> = ({
  brand,
  infoTexts,
  importShipmentInfo,
  shippingPromotion,
  isPreorder,
  exclusivityAvailabilityAgreementTokenPromise,
  isMobile,
  title,
}) => {
  useTraceComponentRender("ShippingDetailsList");
  const { strictLocalize } = useStrictLocalization();
  const { retailerToken } = useRetailer();
  const prefetchPdpApis = useSettingPrefetchPdpApis();
  const [
    exclusivityAvailabilityAgreementToken,
    setExclusivityAvailabilityAgreementToken,
  ] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    exclusivityAvailabilityAgreementTokenPromise.then((token) => {
      if (token !== undefined) {
        setExclusivityAvailabilityAgreementToken(token);
      }
    });
  }, [exclusivityAvailabilityAgreementTokenPromise]);

  const brandToken = brand.token ?? "";
  // Don't need to fetch brand view if prefetchPdpApis is enabled
  const { data, isPending } = useFetchCachedBrandView(
    prefetchPdpApis ? "" : brandToken
  );

  const isInternationalBuyingNudgesV2Enabled =
    useSettingInternationalBuyingNudgesV2();

  // Collect shipping details items
  const shipsFrom: Array<ShippingDetailsItem> = [];

  // Add estimated delivery message
  const estimatedDeliveryMessage = useEstimatedDeliveryMessage(
    data,
    isPreorder
  );

  // Add shipping promotion
  addShippingPromotion(shipsFrom, shippingPromotion, isMobile);

  if (!prefetchPdpApis) {
    const deliveryItem = createEstimatedDeliveryItem(
      estimatedDeliveryMessage,
      isMobile
    );
    if (deliveryItem) {
      shipsFrom.push(deliveryItem);
    }
  } else {
    const prefetchedDeliveryItem = createPrefetchedEstimatedDeliveryItem(
      brand,
      isPreorder,
      strictLocalize,
      isMobile
    );
    if (prefetchedDeliveryItem) {
      shipsFrom.push(prefetchedDeliveryItem);
    }
  }

  // Add lead time item
  const leadTimeItem = createLeadTimeItem(brand, strictLocalize);
  if (leadTimeItem) {
    shipsFrom.push(leadTimeItem);
  }

  // Add info texts or import shipment info
  if (infoTexts) {
    const infoTextsItems = createInfoTextsItems(
      infoTexts,
      brand.token,
      retailerToken,
      exclusivityAvailabilityAgreementToken
    );
    shipsFrom.push(...infoTextsItems);
  } else {
    const importShipmentInfoItem = createImportShipmentInfoItem(
      importShipmentInfo,
      isInternationalBuyingNudgesV2Enabled
    );
    if (importShipmentInfoItem) {
      shipsFrom.push(importShipmentInfoItem);
    }
  }

  if (shipsFrom.length <= 0 || (isPending && !prefetchPdpApis)) {
    return null;
  }

  return <ShippingDetailsListContent shipsFrom={shipsFrom} title={title} />;
};

const CustomLink: React.FC<{
  linkUrl: string | undefined;
  ctaText: string | undefined;
  brandToken: string | undefined;
  type: keyof typeof IInformationMessage.Type | undefined;
  exclusivityAvailabilityAgreementToken: string | undefined;
}> = ({
  linkUrl,
  type,
  ctaText,
  brandToken,
  exclusivityAvailabilityAgreementToken,
}) => {
  if (!ctaText) {
    return null;
  }

  if (
    type === IInformationMessage.Type.EXCLUSIVITY_ACTIVE &&
    exclusivityAvailabilityAgreementToken
  ) {
    return (
      <ExclusivityDetailsLink
        agreementToken={exclusivityAvailabilityAgreementToken}
        brandToken={brandToken}
        origin="product"
        color="inherit"
      >
        {ctaText}
      </ExclusivityDetailsLink>
    );
  }

  if (linkUrl) {
    return (
      <Button
        variant="tertiary"
        linkTo={linkUrl}
        tertiaryColor="inherit"
        target="_blank"
      >
        {ctaText}
      </Button>
    );
  }

  return null;
};

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;
  flex-shrink: 0;
`;

const ListItemNoBullet = styled.li`
  list-style-type: none;
  padding: 0;
  padding-bottom: 8px;
  margin: 0;
`;
